import * as $ from 'jquery';
import {
    FillPatternTypeEnum,
    GeometryTypeEnum,
    LabelsPositionEnum, LegendOrientationEnum,
    LinePatternTypeEnum,
    StyleRuleTypeEnum
} from "../enum/enum";
import {drawDiagonalLines, drawDotted, drawHorizontalLines, drawVerticalLines} from "../draw";
import '/modules/common/css/legend.css'
import '/modules/common/css/legend.responsive.css'
import '/modules/common/css/legend.mobile.css'
import {isGraphicLowContrastOnWhite} from "../colors";
import {Base} from "../Base";
import html2canvas from "html2canvas";
import {Loader} from "../ui/Loader";
import {arrayLast} from "../util";

/**
 * A layer legend
 * @extends Base
 */
class Legend extends Base {
    /**
     * @param translator {Translator} A translator
     * @param isMobileDevice {boolean} true if this is a mobile device. false otherwise
     * @param stylesConfig {Object} The styles configs for the layer
     */
    constructor(isMobileDevice, translator, stylesConfig) {
        super();
        this.isMobileDevice = isMobileDevice;
        this.translator = translator;
        this.stylesConfig = stylesConfig;
        this.onNavigationCallbacks = [];

        // let's create all the elements before the first invocation of onNavigation
        this.getSmallGraphic();
        this.getLargeGraphic();
    }

    getClassName() {
        return "Legend";
    }

    getDescription(){
        const legendConfig = this.stylesConfig[0].getLegend();
        return legendConfig?legendConfig.getLegendDescription():undefined;
    }

    _getGraphic(graphic, small, labelsPosition, removeNoData, forcePolygon) {
        graphic.empty();
        for (let s in this.stylesConfig) {
            const styleConfig = this.stylesConfig[s];
            const rules = this.stylesConfig[s].getRules();
            const geometryType = forcePolygon ? GeometryTypeEnum.POLYGON : styleConfig.getGeometryType();
            const hasContinuous = rules.filter(function (r) {
                return r.getStyleRuleType() === StyleRuleTypeEnum.CONTINUOUS
            }).length > 0;
            const discreteCount = rules.filter(function (r) {
                return r.getStyleRuleType() === StyleRuleTypeEnum.DISCRETE
            }).length;
            const hasDiscrete = discreteCount > 0;
            const barElements = hasDiscrete || hasContinuous ? [] : undefined;
            const barContainer = $('<div class="bar-elements"></div>');
            if (barElements) {
                graphic.append(barContainer);
                if (hasDiscrete)
                    barContainer.addClass('has-discrete');
                if (hasContinuous)
                    barContainer.addClass('has-continuous');
            }

            let labelsPositionForStyle = labelsPosition
                ? labelsPosition
                : (styleConfig.getLegend() ? styleConfig.getLegend().getLabelsPosition() : undefined);

            for (let r in rules) {
                const rule = rules[r];
                const styleRuleType = rule.getStyleRuleType();
                if (styleRuleType === StyleRuleTypeEnum.CONTINUOUS) {
                    barElements.push(this._getContinuousElement(small, rule, geometryType, labelsPositionForStyle, hasDiscrete, Number(r) === 0));
                } else if (styleRuleType === StyleRuleTypeEnum.DISCRETE) {
                    const sizePerc = !hasContinuous
                        ? 100
                        : (discreteCount === 2 ? 25 : 50);
                    barElements.push(this._getDiscreteElement(small, rule, geometryType, labelsPositionForStyle, sizePerc, Number(r) === 0));
                } else if (styleRuleType === StyleRuleTypeEnum.CLASSIFIED) {
                    const classifiedElement = this._getClassifiedElement(small, rule, geometryType, labelsPositionForStyle, styleConfig.getLegend()?styleConfig.getLegend().getLegendOrientation():undefined);
                    graphic.append($('<div class="classified-element"></div>').append(classifiedElement));
                } else if (!removeNoData && styleRuleType === StyleRuleTypeEnum.NODATA) {
                    let noDataElement = this._getNoDataElement(small, rule, geometryType);
                    graphic.append($('<div class="nodata-element"></div>').append(noDataElement));
                } else if (!removeNoData && styleRuleType === StyleRuleTypeEnum.CLUSTER) {
                    let clusterElement = this._getClusterElement(small, rule, geometryType);
                    graphic.append($('<div class="cluster-element"></div>').append(clusterElement));
                } else if (styleRuleType === StyleRuleTypeEnum.SIMPLE) {
                    const simpleElement = this._getSimpleElement(small, rule, geometryType);
                    graphic.append($('<div class="simple-element"></div>').append(simpleElement));
                } else if (styleRuleType === StyleRuleTypeEnum.EXTERNAL_IMAGE) {
                    const externalImageElement = this._getExternalImageElement(small, rule);
                    graphic.append($('<div class="external-image-element"></div>').append(externalImageElement));
                } else if (styleRuleType === StyleRuleTypeEnum.EXTERNAL_DYNAMIC_IMAGE) {
                    const externalDynamicImageElement = this._getExternalDynamicImageElement(small, rule);
                    graphic.append($('<div class="external-image-element external-dynamic-image-element"></div>').append(externalDynamicImageElement));
                }
            }

            if (barElements && barElements.length > 0) {
                for (const be in barElements) {
                    if (barElements[be].hasClass('top-label'))
                        barContainer.addClass('top-label');
                    if (barElements[be].hasClass('bottom-label'))
                        barContainer.addClass('bottom-label');
                    if (barElements[be].hasClass('has-groups'))
                        barContainer.addClass('has-groups');
                    if (barElements[be].hasClass('has-rule-label'))
                        barContainer.addClass('has-rule-label');
                    barContainer.append(barElements[be]);
                }
                if (barElements.length === 1 && geometryType === GeometryTypeEnum.POINT) {
                    barElements[0].css('display', 'flex');
                }
                barElements[0].addClass('first-bar-element');
                arrayLast(barElements).addClass('last-bar-element');
            }
        }
    }

    /**
     * Get a small graphic of the legend (possibly simplified)
     * @return {jQuery}
     */
    getSmallGraphic(exportButton = undefined) {
        if (!this.smallGraphic) {
            this.smallGraphic = $('<div class="layer-legend-graphic-small"></div>');
        }
        this._getGraphic(this.smallGraphic, true, undefined, false, false);
        this._handleExportButton(exportButton, this.smallGraphic);
        this._runOnNavigationCallbacks();
        return this.smallGraphic;
    }

    getChartGraphic(exportButton = undefined) {
        if (!this.chartGraphic) {
            this.chartGraphic = $('<div class="layer-legend-graphic-small"></div>');
        }
        this._getGraphic(this.chartGraphic, true, undefined, true, true);
        this._handleExportButton(exportButton, this.chartGraphic);
        return this.chartGraphic;
    }


    /**
     * Get the full legend graphic
     * @return {jQuery}
     */
    getLargeGraphic(exportButton = undefined) {
        if (!this.largeGraphic) {
            this.largeGraphic = $('<div class="layer-legend-graphic-large"></div>');
        }
        this._getGraphic(this.largeGraphic, false, !this.isMobileDevice ? LabelsPositionEnum.BOTTOM : undefined, false, false);
        this._handleExportButton(exportButton, this.largeGraphic, true);
        this._runOnNavigationCallbacks();
        return this.largeGraphic;
    }

    _handleExportButton(exportButton, graphic, large = false) {
        if (exportButton) {
            exportButton.click(() => {
                const loader = new Loader('export_legend_loader',this.isMobileDevice,$($('body .tumbo')[0]));
                loader.show();
                const element = $('<div></div>')
                    .addClass('indicator-legend')
                    .css('position','fixed')
                    .css('bottom','-1000px')
                    .css('background','transparent')
                    .append(graphic.clone());
                $($('body .tumbo')[0]).append(element);
                if (!large) {
                    element.height(graphic.height() + 50)
                        .width(graphic.width() + 15)
                        .css('padding-top', '25px');
                } else {
                    element.css('height', 'fit-content')
                        .css('width', 'fit-content')
                        .css('min-width', '600px')
                        .css('padding', '5px');
                }

                html2canvas(element[0], {
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: null,
                    scale: 4,
                    dpi: 500
                }).then(canvas => {
                    try {
                        element.remove();
                        const link = document.createElement('a');
                        link.download = 'legend.png';
                        link.href = canvas.toDataURL('image/png');
                        link.click();
                    } catch (e) {
                        this.logError("Error downloading legend image", e)
                        loader.hide();
                        alert(e);
                    } finally {
                        loader.hide();
                    }
                }, error => {
                    element.remove();
                    this.logError("Error rendering image of legend", error)
                    loader.hide();
                    alert(error);
                });
            })
        }
    }

    _isTopLabel(labelsPositionForStyle, vertical, small, ruleEntries, index) {
        let isTopLabel = false;

        if (!labelsPositionForStyle || small && ruleEntries.length > 8)
            isTopLabel = vertical
                ? false
                : ruleEntries.length > 3 && index % 2 !== 0;
        else if (labelsPositionForStyle === LabelsPositionEnum.TOP)
            isTopLabel = true;
        else if (labelsPositionForStyle === LabelsPositionEnum.BOTTOM)
            isTopLabel = false;
        else if (labelsPositionForStyle === LabelsPositionEnum.ALTERNATE)
            isTopLabel = ruleEntries.length > 3 && index % 2 !== 0;
        return isTopLabel;
    }

    _getClassifiedElement(small, rule, geometryType, labelsPositionForStyle, legendOrientation) {
        const classes = rule.getClasses();
        const elementContainer = $('<div class="graphic-element-container"></div>');
        const element = $('<div class="legend-classified-element graphic-element"></div>');

        const vertical = legendOrientation===LegendOrientationEnum.VERTICAL || !small;
        if (vertical)
            element.addClass('vertical');

        const handleLabel = (clazz, index, clazzElement) => {
            if (clazz.getLabel()) {
                const cropSize = vertical
                    ? (small ? 40 : 80)
                    : (!small
                            ? (classes.length > 10 ? 10 : 15)
                            : (classes.length > 8 ? 7 : classes.length > 5 ? 10 : 17)
                    );
                const label = $('<label>' + clazz.getLabel().crop(cropSize) + '</label>');
                const isTopLabel = this._isTopLabel(labelsPositionForStyle, vertical, small, classes, index);
                label.addClass(isTopLabel ? 'top' : 'bottom');
                label.attr('title', clazz.getGroupLabel()?clazz.getGroupLabel():clazz.getLabel());
                clazzElement.append(label);
                clazzElement.addClass(isTopLabel ? 'top-label' : 'bottom-label');
            }
            clazzElement.attr('title', clazz.getGroupLabel()?clazz.getGroupLabel():clazz.getLabel());
        };

        const createNonLinearElement = (clazz, index, fillPattern, linePattern, geometryType) => {
            const clazzElement = $('<div class="clazz-element"></div>');
            if (fillPattern) {
                const GRAPHIC_WIDTH = small
                    ? (geometryType === GeometryTypeEnum.POLYGON ? 50 : 15)
                    : (geometryType === GeometryTypeEnum.POLYGON ? 50 : 25);
                const GRAPHIC_HEIGHT = small
                    ? (geometryType === GeometryTypeEnum.POLYGON ? 20 : 15)
                    : (geometryType === GeometryTypeEnum.POLYGON ? 30 : 25);

                const color = clazz.getBorderColor() ? clazz.getBorderColor() : (fillPattern.getColor() ? fillPattern.getColor() : 'black');
                const width = fillPattern.getWidth() ? fillPattern.getWidth() : 1;
                this._drawFillElement(clazzElement, GRAPHIC_WIDTH - 2, GRAPHIC_HEIGHT - 2, color, width, fillPattern.getFillPatternType(), rule.getCode(), small, geometryType);
                if (clazz.getBorderWidth()) {
                    clazzElement.css('border', clazz.getBorderWidth() + 'px solid ' + color);
                }
                clazzElement.css('width', GRAPHIC_WIDTH + 'px');
            } else {
                if (clazz.getBorderWidth()) {
                    const borderColor = clazz.getBorderColor() ? clazz.getBorderColor() : 'black';
                    clazzElement.css('border', clazz.getBorderWidth() + 'px solid ' + borderColor);
                } else if (isGraphicLowContrastOnWhite(clazz.getBackgroundColor())) {
                    clazzElement.css('border', '1px solid rgba(178, 178, 178, .35)');
                }

                if (linePattern) {
                    if (linePattern.getLinePatternType() === LinePatternTypeEnum.DOTTED)
                        clazzElement.css('border-style', 'dotted');
                    else if (linePattern.getLinePatternType() === LinePatternTypeEnum.DASHED)
                        clazzElement.css('border-style', 'dashed');
                    if (linePattern.getWidth())
                        clazzElement.css('border-width', linePattern.getWidth() + 'px');
                    if (linePattern.getColor())
                        clazzElement.css('border-color', linePattern.getColor());
                }

                clazzElement.css('background-color', clazz.getBackgroundColor());
                if (vertical) {
                    clazzElement.css('width', '50px');
                } else {
                    clazzElement.css('width', 'calc(' + (100.0 / classes.length) + '% - 3px)');
                }

                if (index > 0 && !vertical) {
                    clazzElement.css('margin-left', (3 * classes.length / (classes.length - 1) + 'px'));
                }

                if (geometryType === GeometryTypeEnum.POINT) {
                    const radius = small ? 15 : 25;
                    clazzElement.css('width', radius + 'px')
                        .css('height', radius + 'px')
                        .css('border-radius', '50%');
                }
            }


            if (index > 0 && vertical) {
                clazzElement.css('margin-top', '5px');
            }

            handleLabel(clazz, index, clazzElement);
            return clazzElement;
        };

        function createLineElement(clazz, index, linePattern) {
        }

        if (rule.getLabel()) {
            elementContainer
                .addClass('has-rule-label')
                .append($('<div><label>' + this.translator.translate(rule.getLabel()) + '</label></div>'));
        }

        for (let c in classes) {
            const clazz = classes[c];

            const index = Number(c);

            const linePattern = clazz.getLinePattern();
            const fillPattern = clazz.getFillPattern() ? clazz.getFillPattern() : rule.getFillPattern();
            const clazzElement = geometryType === GeometryTypeEnum.POLYGON
                ? createNonLinearElement(clazz, index, fillPattern, linePattern, GeometryTypeEnum.POLYGON)
                : geometryType === GeometryTypeEnum.POINT
                    ? createNonLinearElement(clazz, index, fillPattern, null, GeometryTypeEnum.POINT)
                    : createLineElement(clazz, index, linePattern);
            element.append(clazzElement);
            if (clazzElement.hasClass('top-label'))
                element.addClass('top-label');
            if (clazzElement.hasClass('bottom-label'))
                element.addClass('bottom-label');
        }
        element.addClass(geometryType.getCode().toLowerCase());
        elementContainer.append(element);
        if (element.hasClass('top-label'))
            elementContainer.addClass('top-label');
        if (element.hasClass('bottom-label'))
            elementContainer.addClass('bottom-label');
        return elementContainer;
    }

    _getContinuousElement(small, rule, geometryType, labelsPositionForStyle, sharesBar, isFirstRule) {
        const ranges = rule.getRanges();
        const elementContainer = !sharesBar ? $('<div class="graphic-element-container"></div>') : undefined;
        const element = $('<div class="legend-continuous-element graphic-element"></div>');
        element.css('width', sharesBar ? '50%' : '100%');
        if (geometryType === GeometryTypeEnum.POINT) {
            const size = small ? 15 : 25;
            element.css('height', size + 'px');
            if (sharesBar) {
                element.css('width', 'calc(50% - 5px)');
                if (isFirstRule) {
                    element.css('margin-right', '5px');
                } else {
                    element.css('margin-left', '5px');
                }
            }
        }

        if (elementContainer && rule.getLabel()) {
            elementContainer
                .addClass('has-rule-label')
                .append($('<div><label>' + this.translator.translate(rule.getLabel()) + '</label></div>'));
        }

        for (let r in ranges) {
            const range = ranges[r];
            const index = Number(r);
            const rangeElement = $('<div class="range-element bar-element"></div>');
            if (range.getBorderWidth()) {
                const color = range.getBorderColor() ? range.getBorderColor() : 'black';
                rangeElement.css('border', range.getBorderWidth() + 'px solid ' + color);
            }
            rangeElement.css('background-image', 'linear-gradient(to right, ' + range.getMinBackgroundColor() + ' , ' + range.getMaxBackgroundColor() + ')');
            rangeElement.css('width', (100.0 / ranges.length) + '%');

            let pointSize = small ? 15 : 25;
            if (index === 0 && geometryType === GeometryTypeEnum.POINT) {
                rangeElement.css('border-top-left-radius', pointSize + 'px');
                rangeElement.css('border-bottom-left-radius', pointSize + 'px');
            }
            if (index === ranges.length - 1 && geometryType === GeometryTypeEnum.POINT) {
                rangeElement.css('border-top-right-radius', pointSize + 'px');
                rangeElement.css('border-bottom-right-radius', pointSize + 'px');
            }

            if (range.getMinLabel()) {
                const minLabel = $('<label class="min">' + this.translator.translate(range.getMinLabel()) + '</label>');
                if (index === 0 && geometryType === GeometryTypeEnum.POINT && sharesBar && !isFirstRule) {
                    minLabel.css('margin-left', '5px');
                }
                rangeElement.append(minLabel);
            }
            if (range.getMaxLabel() !== undefined) {
                const maxLabel = $('<label class="max">' + this.translator.translate(range.getMaxLabel()) + '</label>');
                if (index === ranges.length - 1 && geometryType === GeometryTypeEnum.POINT && sharesBar && isFirstRule) {
                    maxLabel.css('margin-right', '5px');
                }
                rangeElement.append(maxLabel);
            }
            element.append(rangeElement);
        }

        element.addClass(geometryType.getCode().toLowerCase());
        if (elementContainer) {
            elementContainer.append(element);
            return elementContainer;
        } else return element;
    }

    _getDiscreteElement(small, rule, geometryType, labelsPositionForStyle, sizePerc, isFirstRule) {
        const buckets = rule.getBuckets();
        const elementContainer = $('<div class="graphic-element-container"></div>');
        const element = $('<div class="legend-discrete-element graphic-element"></div>');
        elementContainer.css('width', sizePerc + '%');
        element.css('width', '100%');
        if (geometryType === GeometryTypeEnum.POINT) {
            if (sizePerc < 100) {
                element.css('width', 'calc(' + sizePerc + '% - 5px)');
                if (isFirstRule) {
                    element.css('margin-right', '5px');
                } else {
                    element.css('margin-left', '5px');
                }
            }
        }

        let labelCount = 0;

        const handleBucketLabel = (bucket, bucketElement) => {
            const createLabelElement = (text, cropSize) => {
                const label = $('<label>' + text.crop(cropSize) + '</label>');
                if (!bucket.getGroupLabel()) {
                    label.attr('title', text);
                    bucketElement.attr('title', text);
                } else if (small) {
                    label.attr('title', bucket.getGroupLabel());
                    bucketElement.attr('title', bucket.getGroupLabel());
                }
                const isTopLabel = this._isTopLabel(labelsPositionForStyle, false, small, buckets, labelCount);
                label.addClass(isTopLabel ? 'top' : 'bottom');
                return label;
            };

            if (bucket.getMaxLabel() || bucket.getMinLabel()) {
                if (bucket.getMinLabel()) {
                    const minCropSize = small
                        ? buckets.length >= 5 ? 6 : 14
                        : buckets.length >= 7 ? 12 : 20;
                    const leftLabel = createLabelElement(bucket.getMinLabel(), minCropSize);
                    leftLabel.addClass('min');
                    bucketElement.append(leftLabel);
                    labelCount++;
                }
                if (bucket.getMaxLabel()) {
                    const maxCropSize = small
                        ? buckets.length >= 5 ? 6 : 14
                        : buckets.length >= 7 ? 12 : 20;
                    const rightLabel = createLabelElement(bucket.getMaxLabel(), maxCropSize);
                    rightLabel.addClass('max');
                    bucketElement.append(rightLabel);
                    labelCount++;
                }
            } else {
                if (bucket.getLabel() !== undefined) {
                    const cropSize = small
                        ? buckets.length >= 5 ? 10 : 17
                        : buckets.length >= 7 ? 12 : 20;
                    const centerLabel = createLabelElement(bucket.getLabel(), cropSize);
                    bucketElement.append(centerLabel);
                    labelCount++;
                }
            }
        };

        const createPolygonElement = (bucket, index) => {
            const bucketElement = $('<div class="bucket-element bar-element"></div>');
            if (bucket.getBorderWidth()) {
                const color = bucket.getBorderColor() ? bucket.getBorderColor() : 'black';
                bucketElement.css('border', bucket.getBorderWidth() + 'px solid ' + color);
            } else if (isGraphicLowContrastOnWhite(bucket.getBackgroundColor())) {
                bucketElement.css('border', '1px solid rgba(178, 178, 178, .35)');
            }
            bucketElement.css('background-color', bucket.getBackgroundColor())
                .css('width', (100.0 / buckets.length) + '%');

            handleBucketLabel(bucket, bucketElement, index);
            return bucketElement;
        };

        const createPointElement = (bucket, index) => {
            const radius = small ? 15 : 25;
            return createPolygonElement(bucket, index)
                .css('width', radius + 'px')
                .css('height', radius + 'px')
                .css('border-radius', '50%');
        };

        const createLineElement = (bucket, index) => {
            const bucketElement = $('<div class="bucket-element bar-element"><div class="line"></div></div>');
            const color = bucket.getBorderColor() ? bucket.getBorderColor() : 'black';
            if (isGraphicLowContrastOnWhite(color)) {
                bucketElement.addClass('gray-background');
            }
            bucketElement.css('width', (100.0 / buckets.length) + '%');
            $('.line', bucketElement).css('border-top', (bucket.getBorderWidth() * 2) + 'px solid ' + color);

            handleBucketLabel(bucket, bucketElement, index);
            return bucketElement;
        };

        if (rule.getLabel()) {
            elementContainer
                .addClass('has-rule-label')
                .append($('<div class="rule-label"><label>' + this.translator.translate(rule.getLabel()) + '</label></div>'));
        }

        if (!small) {
            const groupElements = {};
            let groupLabel = undefined;
            let idOfFirstBucketInGroup = undefined;
            for (let b in buckets) {
                const bucket = buckets[b];
                if (groupLabel !== bucket.getGroupLabel()) {
                    groupLabel = bucket.getGroupLabel();
                    idOfFirstBucketInGroup = b;
                }
                if (bucket.getGroupLabel()) {
                    if (groupElements[groupLabel + idOfFirstBucketInGroup] === undefined)
                        groupElements[groupLabel + idOfFirstBucketInGroup] = {
                            count: 1,
                            groupLabel: bucket.getGroupLabel()
                        };
                    else groupElements[groupLabel + idOfFirstBucketInGroup].count++;
                }
            }
            if (Object.keys(groupElements).length > 0) {
                element.addClass('has-groups');
                elementContainer.append($('<div class="groups"></div>'));
                elementContainer.addClass('has-groups');
            }
            for (let g in groupElements) {
                const groupElement = $('<div class="group"><label>' + this.translator.translate(groupElements[g].groupLabel) + '</label></div>');
                groupElement.css('width', (groupElements[g].count / buckets.length * 100) + '%');
                $('.groups', elementContainer).append(groupElement);
            }
        }

        for (let b in buckets) {
            const bucket = buckets[b];
            const index = Number(b);
            const bucketElement = geometryType === GeometryTypeEnum.POLYGON
                ? createPolygonElement(bucket, index)
                : geometryType === GeometryTypeEnum.POINT
                    ? createPointElement(bucket, index)
                    : createLineElement(bucket, index);
            element.append(bucketElement);
            if (bucketElement.hasClass('top-label'))
                element.addClass('top-label');
            if (bucketElement.hasClass('bottom-label'))
                element.addClass('bottom-label');
        }

        element.addClass(geometryType.getCode().toLowerCase());
        if (elementContainer) {
            elementContainer.append(element);
            if (element.hasClass('top-label'))
                elementContainer.addClass('top-label');
            if (element.hasClass('bottom-label'))
                elementContainer.addClass('bottom-label');
            if (element.hasClass('has-groups'))
                elementContainer.addClass('has-groups');
            return elementContainer;
        } else return element;
    }

    _getNoDataElement(small, rule, geometryType) {
        return this._getSimpleStyleElement(small, rule, geometryType, rule.getLabel()?rule.getLabel():'layer.legend.nodata', 'nodata', true);
    }

    _getClusterElement(small, rule, geometryType) {
        return this._getSimpleStyleElement(small, rule, geometryType, rule.getLabel()?rule.getLabel():'layer.legend.cluster', 'cluster');
    }

    _getSimpleElement(small, rule, geometryType) {
        return this._getSimpleStyleElement(small, rule, geometryType, rule.getLabel(), 'simple');
    }

    _getSimpleStyleElement(small, rule, geometryType, label, clazz, isNoData = false) {
        const element = $('<div class="legend-' + clazz + '-element graphic-element"></div>');
        const graphicElement = $('<div class="' + clazz + '-element-graphic"></div>');
        if (rule.getBorderWidth()) {
            const color = rule.getBorderColor() ? rule.getBorderColor() : 'black';
            graphicElement.css('border', rule.getBorderWidth() + 'px solid ' + color);
            if (isGraphicLowContrastOnWhite(color)) {
                element.addClass('gray-background');
            }
        }
        graphicElement.css('background-color', rule.getBackgroundColor());
        if (geometryType === GeometryTypeEnum.POLYGON) {
            graphicElement.css('width', (100.0 / rule.length) + '%');
            const linePattern = rule.getLinePattern();
            if (linePattern) {
                if (linePattern.getLinePatternType() === LinePatternTypeEnum.DOTTED)
                    graphicElement.css('border-style', 'dotted');
                else if (linePattern.getLinePatternType() === LinePatternTypeEnum.DASHED)
                    graphicElement.css('border-style', 'dashed');
                if (linePattern.getWidth())
                    graphicElement.css('border-width', linePattern.getWidth() + 'px');
                if (linePattern.getColor())
                    graphicElement.css('border-color', linePattern.getColor());
            }
        } else if (geometryType === GeometryTypeEnum.POINT) {
            const radius = (small ? 15 : 25);
            graphicElement.css('width', radius + 'px');
            graphicElement.css('height', radius + 'px');
            graphicElement.css('border-radius', '50%');
        } else if (geometryType === GeometryTypeEnum.LINE) {
            graphicElement.css('width', (100.0 / rule.length) + '%');
            graphicElement.css('border-left', '0');
            graphicElement.css('border-right', '0');
            graphicElement.css('border-bottom', '0');
            const linePattern = rule.getLinePattern();
            if (linePattern) {
                if (linePattern.getLinePatternType() === LinePatternTypeEnum.DOTTED)
                    graphicElement.css('border-top-style', 'dotted');
                else if (linePattern.getLinePatternType() === LinePatternTypeEnum.DASHED)
                    graphicElement.css('border-top-style', 'dashed');
            }
        }

        const fill = rule.getFillPattern();
        if (fill && (geometryType === GeometryTypeEnum.POLYGON || geometryType === GeometryTypeEnum.POINT)) {
            const width = small
                ? (geometryType === GeometryTypeEnum.POLYGON ? 50 : 15)
                : (geometryType === GeometryTypeEnum.POLYGON ? 80 : 25);
            const height = small
                ? (geometryType === GeometryTypeEnum.POLYGON ? 20 : 15)
                : (geometryType === GeometryTypeEnum.POLYGON ? 30 : 25);
            const color = fill.getColor() ? fill.getColor() : 'black';
            this._drawFillElement(graphicElement, width, height, color, fill.getWidth(), fill.getFillPatternType(), rule.getCode(), small, geometryType);

        }

        const imageUrl = rule.getImageUrl ? rule.getImageUrl() : undefined;
        if (imageUrl && imageUrl.length > 0 && geometryType === GeometryTypeEnum.POINT) {
            graphicElement.append($('<img alt="point legend image" height="' + (small ? 20 : 30) + '" src="' + rule.getImageUrl() + '"/>'));
            graphicElement.addClass('image');
            graphicElement.css('width', 'unset');
            graphicElement.css('height', small ? '20px' : '30px');
            graphicElement.css('border-radius', '0');
        }

        element.append(graphicElement);
        if (label && label.trim().length > 0)
            element.append('<label>' + this.translator.translate(label) + '</label>');
        element.addClass(geometryType.getCode().toLowerCase());
        return element;
    }

    _drawFillElement(element, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, color, thickness, fillType, ruleCode, small, geometryType) {
        const canvas = $('<canvas id="' + fillType.getCode() + "_fill_" + ruleCode + '" width="' + GRAPHIC_WIDTH + '" height="' + GRAPHIC_HEIGHT + '"></canvas>')[0];
        const context = canvas.getContext("2d");
        switch (fillType) {
            case FillPatternTypeEnum.DIAGONAL_CROSS:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_HEIGHT / 5, true);
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_HEIGHT / 5, false);
                break;
            case FillPatternTypeEnum.DIAGONAL:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_HEIGHT / 5 + 1, false);
                break;
            case FillPatternTypeEnum.ANTI_DIAGONAL:
                drawDiagonalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, GRAPHIC_HEIGHT / 5 + 1, true);
                break;
            case FillPatternTypeEnum.HORIZONTAL:
                drawHorizontalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, Math.min(4, Math.ceil(8 / thickness)));
                break;
            case FillPatternTypeEnum.VERTICAL:
                drawVerticalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, Math.min(8, Math.ceil(16 / thickness)));
                break;
            case FillPatternTypeEnum.CROSS:
                drawHorizontalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, Math.min(4, Math.ceil(8 / thickness)));
                drawVerticalLines(context, color, thickness, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, Math.min(8, Math.ceil(16 / thickness)));
                break;
            case FillPatternTypeEnum.DOTTED:
                const NUM_ROWS = small
                    ? (geometryType === GeometryTypeEnum.POLYGON ? 5 : 6)
                    : (geometryType === GeometryTypeEnum.POLYGON ? 7 : 7);
                const NUM_COLS = small
                    ? (geometryType === GeometryTypeEnum.POLYGON ? 13 : 6)
                    : (geometryType === GeometryTypeEnum.POLYGON ? 19 : 7);
                drawDotted(context, color, GRAPHIC_WIDTH, GRAPHIC_HEIGHT, NUM_ROWS, NUM_COLS, 2);
                break;
            default:
                break;
        }
        element.append($(canvas));
    }

    _getExternalImageElement(small, rule) {
        const element = $('<div class="legend-external-image-element graphic-element"></div>');
        const graphicElement = $('<div class="external-image-element-graphic"></div>');
        graphicElement.append($('<img alt="legend image" src="' + rule.getImageUrl() + '"/>'));
        element.append(graphicElement);
        return element;
    }

    _getExternalDynamicImageElement(small, rule) {
        const element = $('<div class="legend-external-image-element graphic-element"></div>');
        const graphicElement = $('<div class="external-image-element-graphic"></div>').append($('<img alt="legend image" src=""/>'));
        element.append(graphicElement);
        this.onNavigationCallbacks.push((ignored1, ignored2, extentWgs84) => {
            const imageUrl = rule.getImageUrl();
            const extent = extentWgs84 ? extentWgs84 : [-180, -90, 180, 90];
            $('img', graphicElement).attr('src', imageUrl.replace('_EXTENT_', extent.join('+')));
        });
        return element;
    }

    /**
     * To be called by AbstractLayer when there is navigation on the map
     * It should not be called more than 1 time every 500ms
     * @param zoomLevel {number} The new zoom level (integer)
     * @param centerWgs84 {Array<number>} The new center coordinates (WGS84)
     * @param extentWgs84 {Array<number>} The new map extent (WGS84)
     */
    onNavigation(zoomLevel, centerWgs84, extentWgs84) {
        this.zoomLevel = zoomLevel;
        this.centerWgs84 = centerWgs84;
        this.extentWgs84 = extentWgs84;
        this._runOnNavigationCallbacks();
    }

    _runOnNavigationCallbacks() {
        if(this.zoomLevel!==undefined && this.zoomLevel!==null && this.centerWgs84 && this.extentWgs84)
            for (let f in this.onNavigationCallbacks) {
                this.onNavigationCallbacks[f](this.zoomLevel, this.centerWgs84, this.extentWgs84);
            }
    }
}

export {Legend}